/**
 * HTTP请求追踪管理器
 * @author Nemo
 * @description 追踪所有HTTP请求的完整生命周期，用于错误诊断和埋点
 */
class RequestTracker {
  constructor(config = {}) {
    // 请求映射表: Map<requestId, RequestInfo>
    this.requests = new Map();

    // 配置项
    this.config = {
      cleanupInterval: 30000, // 清理间隔30秒
      pendingCheckInterval: 30000, // 超时检查间隔30秒
      longPendingThreshold: 60000, // 超过60秒视为长时间未响应
      maxResponseSize: 1000, // 响应数据最大保存1000字符
      ...config,
    };

    // 启动定时器
    this.startTimers();
  }

  /**
   * 生成唯一请求ID
   * @returns {string} 请求ID（格式：时间戳-随机数）
   */
  generateRequestId() {
    return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  }

  /**
   * 记录请求发起
   * @param {Object} config - axios请求配置
   * @returns {string} requestId
   */
  trackRequest(config) {
    const requestId = this.generateRequestId();

    this.requests.set(requestId, {
      requestId,
      url: config.url,
      method: config.method?.toUpperCase(),
      startTime: Date.now(),
      status: 'initiated', // initiated | completed | failed
      data: this.sanitizeData(config.data), // 脱敏后的请求数据
    });

    return requestId;
  }

  /**
   * 更新请求状态
   * @param {string} requestId - 请求ID
   * @param {Object} updates - 更新内容
   */
  updateRequest(requestId, updates) {
    const request = this.requests.get(requestId);
    if (request) {
      Object.assign(request, updates, {
        duration: Date.now() - request.startTime,
      });
    }
  }

  /**
   * 标记请求完成
   * @param {string} requestId - 请求ID
   * @param {Object} response - axios响应对象
   */
  markCompleted(requestId, response) {
    this.updateRequest(requestId, {
      status: 'completed',
      httpStatus: response.status,
      statusText: response.statusText,
    });
  }

  /**
   * 标记请求失败
   * @param {string} requestId - 请求ID
   * @param {Error} error - 错误对象
   */
  markFailed(requestId, error) {
    this.updateRequest(requestId, {
      status: 'failed',
      error: {
        message: error.message,
        code: error.code,
        httpStatus: error.response?.status,
      },
    });
  }

  /**
   * 检查长时间未响应的请求
   * @returns {Array} 超时的请求列表
   */
  checkPendingRequests() {
    const now = Date.now();
    const longPendingRequests = [];

    this.requests.forEach((request) => {
      if (request.status === 'initiated' && !request.checked) {
        const duration = now - request.startTime;
        if (duration > this.config.longPendingThreshold) {
          longPendingRequests.push({
            ...request,
            duration,
          });
          // 标记为已检测，避免重复上报
          request.checked = true;
        }
      }
    });

    return longPendingRequests;
  }

  /**
   * 清理已完成/失败的请求记录
   */
  cleanup() {
    const now = Date.now();
    const toDelete = [];

    this.requests.forEach((request, requestId) => {
      // 完成/失败且距离现在超过30秒的记录
      if (request.status !== 'initiated' &&
        now - request.startTime > this.config.cleanupInterval) {
        toDelete.push(requestId);
      }
    });

    toDelete.forEach(id => this.requests.delete(id));
  }

  /**
   * 数据脱敏
   * @param {any} data - 原始数据
   * @returns {string} 脱敏后的数据
   */
  sanitizeData(data) {
    if (!data) return null;

    try {
      const str = typeof data === 'string' ? data : JSON.stringify(data);
      // 限制大小
      return str.substring(0, 500);
    } catch (e) {
      return '[Unserializable]';
    }
  }

  /**
   * 启动定时器
   */
  startTimers() {
    // 定期清理
    this.cleanupTimer = setInterval(() => {
      this.cleanup();
    }, this.config.cleanupInterval);

    // 定期检查超时
    this.pendingCheckTimer = setInterval(() => {
      const longPending = this.checkPendingRequests();
      // 超时请求将由外部处理（发送埋点）
      if (longPending.length > 0 && this.onLongPending) {
        this.onLongPending(longPending);
      }
    }, this.config.pendingCheckInterval);
  }

  /**
   * 停止定时器（用于清理）
   */
  destroy() {
    if (this.cleanupTimer) clearInterval(this.cleanupTimer);
    if (this.pendingCheckTimer) clearInterval(this.pendingCheckTimer);
    this.requests.clear();
  }

  /**
   * 获取请求信息
   * @param {string} requestId - 请求ID
   * @returns {Object|null} 请求信息
   */
  getRequest(requestId) {
    return this.requests.get(requestId) || null;
  }

  /**
   * 获取统计信息
   * @returns {Object} 统计数据
   */
  getStats() {
    let initiated = 0;
    let completed = 0;
    let failed = 0;

    this.requests.forEach(req => {
      if (req.status === 'initiated') {
        initiated += 1;
      } else if (req.status === 'completed') {
        completed += 1;
      } else if (req.status === 'failed') {
        failed += 1;
      }
    });

    return {
      total: this.requests.size,
      initiated,
      completed,
      failed,
    };
  }
}

// 导出单例
export const requestTracker = new RequestTracker();
export default requestTracker;
